// @flow strict

import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Helmet } from 'react-helmet';

import { useLanguageContext } from '~contexts/language-context';
import { createLinkPath } from '~utils/helpers/create-link-path';

import type { Alternates } from '~types/post';

/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

/**
 * Type for html meta tags.
 */
type MetaTags = Array<{ name?: string, property?: string, content: ?string }>;

/**
 * Type for SEO relevant information
 */
export type SEOFields = {
  title: string,
  alternates: Alternates,
  description: string,
  canonical?: string,
  meta?: MetaTags,
  ldJson?: {
    author: string,
    url: string,
    image: string,
  },

  faqLdJson?: Array<{ question: string, answer: string }>,
};

/**
 * Type of component properties
 */
type SEOProps = {
  fields: SEOFields,
};

const locales = {
  de: 'de_DE',
  en: 'en_US',
};

/**
 * Component to add SEO specific information to page head.
 *
 * @param {SEOProps} props - component properties
 *
 * @returns {Node}
 */
export const SEO = ({ fields }: SEOProps) => {
  const { description, canonical, alternates, meta, title, ldJson, faqLdJson } =
    fields;

  // get static data
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          domain
          twitter {
            account
            card
          }
          emails {
            de
            en
          }
        }
      }
    }
  `);

  const { title: siteTitle, twitter, emails, domain } = data.site.siteMetadata;

  const { language } = useLanguageContext();

  // gather meta tags
  const metaTags: MetaTags = [
    {
      name: `description`,
      content: description,
    },
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:description`,
      content: description,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:locale`,
      content: language,
    },
    {
      property: `og:site_name`,
      content: siteTitle,
    },
    {
      name: `twitter:card`,
      content: twitter.card,
    },
    {
      name: `twitter:creator`,
      content: `@${twitter.account}`,
    },
    {
      name: `twitter:site`,
      content: `@${twitter.account}`,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:description`,
      content: description,
    },
    {
      name: 'locale',
      content: `${locales[language]}`,
    },
    {
      name: 'copyright',
      content: `OMQ Team, Berlin, Germany`,
    },
    {
      name: 'email',
      content: `${emails[language]}`,
    },
    ...(meta || []),
  ];

  if (!metaTags.find((tag) => tag.name === 'author')) {
    metaTags.push({
      name: 'author',
      content: 'OMQ Team, Berlin, Germany',
    });
  }

  // generate google structured data
  let googleStructuredData = '';
  if (ldJson != null) {
    googleStructuredData = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: `${title}`,
      description: `${description || ''}`,
      url: `${domain}${ldJson.url}`,
      datePublished: ldJson.datePublished,
      author: { '@type': 'Person', name: `${ldJson.author}` },
      image: `${domain}${ldJson.image}`,
      ...(ldJson?.video && { video: ldJson.video }),
      publisher: {
        '@type': 'Organization',
        name: 'OMQ',
        url: 'https://www.omq.ai',
        logo: {
          '@type': 'ImageObject',
          url: 'https://omq.ai/images/menu/omq.png',
          width: '252',
          height: '94',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id':
          language === 'de'
            ? 'https://www.omq.ai/de/blog'
            : 'https://www.omq.ai/blog',
      },
    });
  }

  // generate faq snippet, if available
  let faqStructuredData = null;
  if (faqLdJson != null) {
    faqStructuredData = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqLdJson.map(({ question, answer }) => {
        return {
          '@type': 'Question',
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer,
          },
        };
      }),
    });
  }

  const xDefaultHref =
    alternates.en != null
      ? createLinkPath({ path: alternates.en, domain })
      : createLinkPath({ path: alternates.de, domain });

  return (
    <Helmet
      htmlAttributes={{
        lang: language,
      }}
      title={title}
      meta={metaTags}
    >
      {/* add alternate URLs */}
      {Object.entries(alternates)
        .filter(([lang]) => lang !== 'fallback')
        .map(([lang, href]) => {
          return (
            <link
              key={lang}
              rel="alternate"
              /* $FlowExpectedError */
              href={createLinkPath({ path: href, domain })}
              hrefLang={lang}
            />
          );
        })}
      <link rel="alternate" href={xDefaultHref} hrefLang="x-default" />
      {canonical != null && <link rel="canonical" href={canonical} />}
      <script type="application/ld+json">{googleStructuredData}</script>
      {faqStructuredData != null && (
        <script type="application/ld+json">{faqStructuredData}</script>
      )}
    </Helmet>
  );
};
